import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Smartphone } from 'lucide-react';

/**
 * ReturnToAppButton
 *
 * Shown ONLY when the user arrived from the Nerd Nation mobile app (external
 * browser handoff via `openWebappWithAuth`). After a 10-second countdown it
 * automatically deep-links back into the native app so the student never has
 * to manually close the browser.
 *
 * The native app's Capacitor `AppState` listener will detect the resume and
 * refresh the user's subscription/profile so the new plan shows up instantly.
 *
 * Registered in `AndroidManifest.xml` with scheme="com.nerdnation.app"
 */
const AUTO_RETURN_SECONDS = 10;

const ReturnToAppButton = () => {
  // Only render when the user came from the mobile app.
  // `mobile_app_session` is set by handleMobileAuthToken() in mobileUtils.js
  // when the URL contains ?source=mobile_app&token=...
  const cameFromApp =
    typeof window !== 'undefined' &&
    window.localStorage &&
    window.localStorage.getItem('mobile_app_session') === 'true';

  const [seconds, setSeconds] = useState(AUTO_RETURN_SECONDS);

  const returnToApp = () => {
    // Deep-link back into the installed Android app.
    window.location.href = 'com.nerdnation.app://payment-complete';
    // Best-effort fallback in case the deep link doesn't fire (rare)
    setTimeout(() => {
      try { window.close(); } catch (e) { /* noop */ }
    }, 700);
  };

  useEffect(() => {
    if (!cameFromApp) return;
    if (seconds <= 0) {
      returnToApp();
      return;
    }
    const t = setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [cameFromApp, seconds]);

  if (!cameFromApp) return null;

  return (
    <Button
      data-testid="return-to-app-btn"
      onClick={returnToApp}
      className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold py-6 text-lg"
    >
      <Smartphone className="h-5 w-5 mr-2" />
      Returning to App in {seconds}s… (tap to skip)
    </Button>
  );
};

export default ReturnToAppButton;
